import React, { useState } from 'react';
import { FEEDBACK_ADD_API_URL } from '../../utils/apiUrls';
import { postRequest } from '../../utils/http';

interface FeedbackFormData {
	agentName: string;
	agentEmail: string;
	feedbackText: string;
}

interface ShareFeedbackProps {
	loggedInUserData?: {
		name?: string;
		email?: string;
	};
}

const ShareFeedback: React.FC<ShareFeedbackProps> = ({ loggedInUserData = {} }) => {
	const [formData, setFormData] = useState<FeedbackFormData>({
		agentName: loggedInUserData.name || '',
		agentEmail: loggedInUserData.email || '',
		feedbackText: '',
	});

	const [errors, setErrors] = useState<Partial<FeedbackFormData>>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submissionStatus, setSubmissionStatus] = useState<'success' | 'error' | null>(null);

	const validate = (data: FeedbackFormData) => {
		const feedbackFormErrors: Partial<FeedbackFormData> = {};
		if (!data.agentName) feedbackFormErrors.agentName = 'Agent name is required';
		if (!data.agentEmail) feedbackFormErrors.agentEmail = 'Email is required';
		else if (!/\S+@\S+\.\S+/.test(data.agentEmail)) feedbackFormErrors.agentEmail = 'Invalid email address';
		if (!data.feedbackText) feedbackFormErrors.feedbackText = 'Feedback details are required';
		return feedbackFormErrors;
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const validationErrors = validate(formData);
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}
		setIsSubmitting(true);
		try {
			const response = await postRequest(FEEDBACK_ADD_API_URL, {
				agent_name: formData.agentName,
				email: formData.agentEmail,
				feedback_text: formData.feedbackText,
			});
			setSubmissionStatus('success');
			setFormData({
				agentName: loggedInUserData.name || '',
				agentEmail: loggedInUserData.email || '',
				feedbackText: '',
			});
		} catch (error) {
			console.error('Error submitting feedback:', error);
			setSubmissionStatus('error');
		}
		setIsSubmitting(false);
	};

	return (
		<div className="">
			<h3 className="mb-2 text-lg leading-6 text-black font-semibold capitalize">Share your feedback</h3>
			<form className="space-y-3" onSubmit={handleSubmit}>
				<div>
					<label htmlFor="agentName" className="block text-xs font-semibold text-gray-900">
						Agent Name
					</label>
					<div className="mt-2">
						<input
							id="agentName"
							name="agentName"
							type="text"
							value={formData.agentName}
							onChange={handleChange}
							className={`block w-full rounded-md border ${errors.agentName ? 'border-red-500' : 'border-gray-300'} py-1.5 text-gray-900 placeholder:text-gray-400 text-xs leading-6 px-2`}
						/>
						{errors.agentName && <div className="text-red-500 text-xs">{errors.agentName}</div>}
					</div>
				</div>
				<div>
					<label htmlFor="agentEmail" className="block text-xs font-semibold text-gray-900">
						Agent Email
					</label>
					<div className="mt-2">
						<input
							id="agentEmail"
							name="agentEmail"
							type="email"
							value={formData.agentEmail}
							onChange={handleChange}
							className={`block w-full rounded-md border ${errors.agentEmail ? 'border-red-500' : 'border-gray-300'} py-1.5 text-gray-900 placeholder:text-gray-400 text-xs leading-6 px-2`}
						/>
						{errors.agentEmail && <div className="text-red-500 text-xs">{errors.agentEmail}</div>}
					</div>
				</div>
				<div>
					<label htmlFor="feedbackText" className="block text-xs font-semibold text-gray-900">
						Feedback Details
					</label>
					<div className="mt-2">
						<textarea
							rows={2}
							id="feedbackText"
							name="feedbackText"
							value={formData.feedbackText}
							onChange={handleChange}
							className={`block w-full rounded-md border ${errors.feedbackText ? 'border-red-500' : 'border-gray-300'} py-1.5 text-gray-900 placeholder:text-gray-400 text-xs leading-6 px-2`}
						/>
						{errors.feedbackText && <div className="text-red-500 text-xs">{errors.feedbackText}</div>}
					</div>
				</div>
				<button
					type="submit"
					className="inline-flex justify-center rounded-3xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white outline-none"
					disabled={isSubmitting}
				>
					Submit
				</button>
				{submissionStatus === 'success' && (
					<div className="text-gray-700 text-xs mt-2 font-medium">Feedback submitted successfully!</div>
				)}
				{submissionStatus === 'error' && (
					<div className="text-red-500 text-xs mt-2 font-medium">Failed to submit feedback. Please try again.</div>
				)}
			</form>
		</div>
	);
};

export default ShareFeedback;
